// client/src/pages/Game/Battlefield/hooks/useBattlefieldDragDrop.js

import { useState, useRef, useCallback, useEffect } from 'react';
import { useNotification } from '../../../../hooks/useNotification';
import { animateFlight, flashZone, flyCardToElement } from '../utils/cardFlight';
import { createTargetReticle, ensureReticleStyles } from '../utils/targetReticle';
import { evaluateDropFeedback } from '../utils/targetFeedback';

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

/** R53e — STEALTH/FLIGHT : insaisissable, ne peut être bloqué que par un
 * défenseur possédant lui-même Discrétion/Vol (miroir de CombatSystem). */
const hasStealthAbility = (c) => Array.isArray(c && c.abilities)
  && c.abilities.some(a => a === 'STEALTH' || a === 'FLIGHT');

// ─── Sorts ────────────────────────────────────────────────────
// PLAY_SPELL est géré par le serveur ({cardInstanceId, targetInstanceId}).
// Cibles « _SINGLE » → drop sur une carte précise ; les autres cibles
// (zones, joueurs, global) → drop n'importe où sur le plateau.
const isSpellCard = (card) =>
  !!(card && (card.type === 'SPELL' || card.cardType === 'SPELL'));

const getSpellTargetNeeds = (card) => {
  const targets = new Set(
    (card?.effects || []).map(e => e && e.target).filter(Boolean)
  );
  const needsEnemy = targets.has('ENEMY_SINGLE');
  const needsAlly = targets.has('ALLY_SINGLE');
  return { targets, needsEnemy, needsAlly, needsSpecific: needsEnemy || needsAlly };
};

/**
 * Drag & drop des cartes du battlefield.
 *
 * IMPORTANT : le drag de cartes est une interaction de GAMEPLAY.
 * Il est actif en permanence — y compris en mode dev. Le mode dev
 * déplace les ZONES (containers), jamais les cartes : les cartes
 * interceptent le pointerdown en priorité (stopPropagation).
 *
 * Architecture anti-fuite (le fantôme DOIT toujours disparaître) :
 *  - `activeGhostRef` référence le noeud DOM du fantôme courant.
 *    Toute nouvelle interaction le supprime physiquement du DOM.
 *  - Les vols (animateFlight) reçoivent un onCancel qui supprime
 *    l'élément — une annulation n'orphanise jamais le fantôme.
 *  - Les listeners mouse/touch sont attachés à `window` : un relâcher
 *    hors du battlefield (ou une perte de focus) termine toujours le drag.
 *  - Un seuil de 6px sépare le clic du drag : un simple clic n'affiche
 *    jamais de fantôme.
 *
 * Au relâcher sur une zone valide, la carte est "jetée" : elle file
 * avec la vitesse du doigt/souris et retombe en arc de cercle sur
 * la zone cible (physique projectile). Sur un drop invalide, elle
 * revient en arc vers sa place dans la main.
 */
const useBattlefieldDragDrop = (gameState, performGameAction, refreshGameState) => {
  const { error: showError } = useNotification();
  const [draggingCard, setDraggingCard] = useState(null);
  const draggingCardRef = useRef(null); // source de vérité (immune aux closures périmées)

  // ── État du drag ──
  const DRAG_THRESHOLD_PX = 6;
  const pendingDragRef = useRef(null);     // {card, x, y, sourceEl} — mousedown sans mouvement
  const dragStartPosRef = useRef({ x: 0, y: 0 });
  const dragCurrentPosRef = useRef({ x: 0, y: 0 });
  const isDraggingRef = useRef(false);

  // ── Moteur d'animation du drag ──
  const sourceElementRef = useRef(null);   // élément DOM de la carte source (retour à la main)
  const activeGhostRef = useRef(null);     // LE fantôme DOM courant — toujours supprimable
  // R34ll : réticule de ciblage (cartes en main)
  const reticleRef = useRef(null);         // contrôleur du réticule
  const reticleSourceElRef = useRef(null); // carte source .hand-card (classe à retirer)
  const velocityRef = useRef({ x: 0, y: 0 }); // px/ms, lissée
  const samplesRef = useRef([]);           // derniers échantillons du pointeur
  const tiltRef = useRef(0);               // inclinaison courante du ghost (deg)
  const targetTiltRef = useRef(0);         // inclinaison visée
  const tiltLoopRef = useRef(null);        // rAF id
  const flightCancelRef = useRef(null);    // vol en cours (annulé si nouveau drag)
  const suppressNextClickRef = useRef(false); // true juste après un drag → bloque le click

  // ── Nettoyage global au démontage : aucun fantôme ne survit ──
  useEffect(() => () => {
    if (flightCancelRef.current) flightCancelRef.current.cancel();
    if (activeGhostRef.current) activeGhostRef.current.remove();
    activeGhostRef.current = null;
    if (tiltLoopRef.current) cancelAnimationFrame(tiltLoopRef.current);
    if (reticleRef.current) { reticleRef.current.destroy(); reticleRef.current = null; }
    if (reticleSourceElRef.current) reticleSourceElRef.current.classList.remove('reticle-drag-source', 'reticle-drag-invalid');
    document.querySelectorAll('.active-drop-target').forEach(z => z.classList.remove('active-drop-target'));
  }, []);

  // Fonction pour déterminer l'élément d'une carte
  const getCardElement = useCallback((card) => {
    if (!card || !card.element) return "fire";

    const elementStr = typeof card.element === 'string'
      ? card.element.toLowerCase()
      : String(card.element).toLowerCase();

    const elementMap = {
      'feu': 'fire',
      'eau': 'water',
      'terre': 'earth',
      'vent': 'air',
      'lumière': 'light',
      'ténèbres': 'dark',
      'arc-en-ciel': 'rainbow'
    };

    return elementMap[elementStr] ||
      (["fire", "water", "earth", "air", "light", "dark", "rainbow"].includes(elementStr)
        ? elementStr
        : "fire");
  }, []);

  // R34ll2 : carte trop chère → flash ROUGE sur elle + BRILLANCE BLANCHE
  // sur toutes les cartes jouables (pour guider le joueur).
  const flashUnaffordable = useCallback((sourceEl, card) => {
    if (sourceEl) {
      sourceEl.classList.add('hand-card-unaffordable');
      setTimeout(() => sourceEl.classList.remove('hand-card-unaffordable'), 1000);
    }
    // Brillance blanche sur les cartes abordables
    const handCards = document.querySelectorAll('.player-hand .hand-card');
    const played = [];
    handCards.forEach(el => {
      const cost = parseInt(el.dataset.cardCost, 10);
      if (Number.isFinite(cost) && cost <= (gameState?.playerEnergy || 0)
        + (card && card.type !== 'CHARACTER' ? (gameState?.playerSpellMana || 0) : 0)) {
        el.classList.add('hand-card-playable-glow');
        played.push(el);
      }
    });
    setTimeout(() => played.forEach(el => el.classList.remove('hand-card-playable-glow')), 1200);
  }, [gameState]);

  // R34ll : retirer le réticule et la surbrillance de la carte source (idempotent)
  const killReticle = useCallback(() => {
    if (reticleRef.current) { reticleRef.current.destroy(); reticleRef.current = null; }
    if (reticleSourceElRef.current) {
      reticleSourceElRef.current.classList.remove('reticle-drag-source');
      reticleSourceElRef.current = null;
    }
  }, []);

  // Supprimer physiquement le fantôme courant (idempotent)
  const killGhost = useCallback(() => {
    if (activeGhostRef.current) {
      activeGhostRef.current.remove();
      activeGhostRef.current = null;
    }
  }, []);

  // Créer un élément fantôme pour le drag
  const createGhostElement = useCallback((card, sourceElement) => {
    killGhost();

    // Créer un nouvel élément
    const ghost = document.createElement('div');
    ghost.className = 'dragging-card';

    // Taille = taille réelle de la carte source (cohérence cqw)
    const rect = sourceElement?.getBoundingClientRect();
    const ghostW = rect?.width || 80;
    const ghostH = rect?.height || 120;

    // Obtenir l'image de la carte
    const img = document.createElement('img');
    img.src = card.imageUrl || `/api/static/images/cards/${getCardElement(card)}.svg`;
    img.onerror = () => {
      img.src = `/api/static/images/card-backs/fire-back.svg`;
    };

    ghost.appendChild(img);

    // Ajouter les statistiques au fantôme quand c'est un monstre
    if (card.type === 'CHARACTER' && (card.attack !== undefined || card.defense !== undefined)) {
      const statsContainer = document.createElement('div');
      statsContainer.className = 'ghost-monster-stats';
      statsContainer.style.cssText = `
        position:absolute;left:0;right:0;bottom:0;
        display:flex;justify-content:space-between;
        background-color:rgba(0,0,0,0.7);padding:3px;z-index:9999;
      `;

      const attackStat = document.createElement('span');
      attackStat.className = 'ghost-attack';
      attackStat.style.cssText = `
        background-color:rgba(255,0,0,0.7);color:white;padding:2px 5px;
        border-radius:3px;font-weight:bold;font-size:11px;
      `;
      attackStat.textContent = card.attack || 0;

      const defenseStat = document.createElement('span');
      defenseStat.className = 'ghost-defense';
      defenseStat.style.cssText = `
        background-color:rgba(0,0,102,0.7);
        color:${card.originalDefense && card.defense < card.originalDefense ? '#ff3838' : 'white'};
        padding:2px 5px;border-radius:3px;font-weight:bold;font-size:11px;
      `;
      defenseStat.textContent = card.defense || 0;

      statsContainer.appendChild(attackStat);
      statsContainer.appendChild(defenseStat);
      ghost.appendChild(statsContainer);
    }

    // Si c'est une carte en main, ajouter le coût
    if (card.location === 'hand' && card.cost !== undefined) {
      const costElement = document.createElement('div');
      costElement.className = 'ghost-cost';
      costElement.style.cssText = `
        position:absolute;top:-10px;right:-8px;width:20px;height:28px;
        clip-path:polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
        background:radial-gradient(circle at 30% 30%, #80c8ff, #0066cc);
        border:1px solid white;color:white;font-weight:bold;
        display:flex;justify-content:center;align-items:center;
        box-shadow:0 0 8px #0099ff;z-index:50;font-size:15px;
        text-shadow:0 1px 1px rgba(0,0,0,0.7);
      `;
      costElement.textContent = card.cost;
      ghost.appendChild(costElement);
    }

    // Rôle attaquant/défenseur
    if (card.role) {
      const roleIndicator = document.createElement('div');
      roleIndicator.className = `ghost-role ${card.role}`;
      roleIndicator.style.cssText = `
        position:absolute;top:0;right:0;
        background-color:${card.role === 'attacker' ? 'rgba(255,0,0,0.7)' : 'rgba(0,0,255,0.7)'};
        color:white;padding:1px 3px;font-size:9px;border-radius:0 0 0 3px;
        font-weight:bold;text-transform:uppercase;z-index:5;
      `;
      roleIndicator.textContent = card.role === 'attacker' ? 'ATK' : 'DEF';
      ghost.appendChild(roleIndicator);
    }

    // Styles généraux du fantôme
    ghost.style.cssText += `
      position:fixed;
      left:-1000px;
      top:-1000px;
      pointer-events:none;
      z-index:9999;
      width:${ghostW}px;
      height:${ghostH}px;
      border-radius:6px;
      overflow:visible;
      will-change:left,top,transform;
    `;
    ghost.style.boxShadow = '0 0 20px rgba(90, 205, 255, 0.65), 0 10px 22px rgba(0,0,0,0.55)';

    // Bordure de rôle pour les cartes en zone de bataille
    if (card.zone === 'battle') {
      if (card.role === 'attacker') {
        ghost.style.border = '2px solid rgba(255, 0, 0, 0.8)';
      } else if (card.role === 'defender') {
        ghost.style.border = '2px solid rgba(0, 0, 255, 0.8)';
      }
    }

    document.body.appendChild(ghost);

    activeGhostRef.current = ghost;
    return ghost;
  }, [getCardElement, killGhost]);

  // ── Boucle d'inclinaison du ghost (tilt selon la vitesse horizontale) ──
  const startTiltLoop = useCallback((ghost) => {
    const frame = () => {
      tiltRef.current += (targetTiltRef.current - tiltRef.current) * 0.18;
      if (ghost.isConnected) {
        ghost.style.transform =
          `translate(-50%, -50%) rotate(${tiltRef.current.toFixed(2)}deg) scale(1.07)`;
      }
      tiltLoopRef.current = requestAnimationFrame(frame);
    };
    if (tiltLoopRef.current) cancelAnimationFrame(tiltLoopRef.current);
    tiltLoopRef.current = requestAnimationFrame(frame);
  }, []);

  const stopTiltLoop = useCallback(() => {
    if (tiltLoopRef.current) {
      cancelAnimationFrame(tiltLoopRef.current);
      tiltLoopRef.current = null;
    }
  }, []);

  // ── Enregistrer un échantillon de position → vélocité lissée + tilt ──
  const recordPointerSample = useCallback((x, y) => {
    const now = performance.now();
    const samples = samplesRef.current;
    samples.push({ x, y, t: now });
    // Garder ~100ms d'historique
    while (samples.length > 2 && now - samples[0].t > 100) {
      samples.shift();
    }
    if (samples.length >= 2) {
      const a = samples[0];
      const b = samples[samples.length - 1];
      const dt = Math.max(b.t - a.t, 1);
      const vx = (b.x - a.x) / dt;
      const vy = (b.y - a.y) / dt;
      velocityRef.current = {
        x: velocityRef.current.x * 0.65 + vx * 0.35,
        y: velocityRef.current.y * 0.65 + vy * 0.35,
      };
      targetTiltRef.current = clamp(velocityRef.current.x * 14, -28, 28);
    }
  }, []);

  // ═══════════ Fonctions d'action (gameplay) ═══════════

  // Fonction pour attaquer
  const handleAttack = useCallback((attackingCard, target = null) => {
    if (!attackingCard) return;

    console.log(`[DragDrop] Attaque avec carte à la position ${attackingCard.position}, cible: ${target}`);

    if (gameState.activePlayer !== 'player') {
      showError("Ce n'est pas votre tour");
      return;
    }

    if (!gameState.playerHasAttack) {
      showError("Vous n'avez pas le jeton d'attaque ce tour-ci");
      return;
    }

    // Règle : celui qui commence ne peut pas attaquer au premier tour
    if (gameState.turn === 1) {
      showError("Attaque interdite au premier tour de jeu");
      return;
    }

    if (attackingCard.hasAttacked) {
      showError("Ce monstre a déjà attaqué ce tour");
      return;
    }

    // Ordre dans la zone de combat : index d'insertion calcule depuis le
    // pointeur (gauche / droite / entre les cartes deja declarees).
    const battleCards = gameState.playerField
      .filter(c => c.zone === 'battle')
      .sort((a, b) => (a.battleOrder ?? a.position) - (b.battleOrder ?? b.position));
    let battleOrder;
    {
      const dropX = (dragCurrentPosRef.current && dragCurrentPosRef.current.x) || null;
      if (dropX === null) {
        battleOrder = battleCards.length;
      } else {
        let idx = battleCards.length;
        for (const bc of battleCards) {
          const el = document.querySelector('[data-instance-id="' + (bc.instanceId || bc.id) + '"]');
          if (el) {
            const r = el.getBoundingClientRect();
            if (r.left + r.width / 2 < dropX) idx = battleCards.indexOf(bc) + 1;
          }
        }
        battleOrder = Math.min(idx, battleCards.length);
      }
    }

    performGameAction('ATTACK', {
      position: attackingCard.position,
      battleOrder,
      targetPosition: target === 'opponent' ? 'direct' : null
    }).then(refreshGameState)
      .catch(err => {
        console.error("[DragDrop] Erreur lors de la préparation d'attaque:", err);
        showError("Erreur lors de la préparation d'attaque");
      });
  }, [gameState, performGameAction, refreshGameState, showError]);

  // Lancer un sort depuis la main (PLAY_SPELL)
  const handleCastSpell = useCallback((card, targetInfo = null) => {
    if (!card || !gameState) return;

    console.log(`[DragDrop] Lancement du sort ${card.name}`, targetInfo || 'sans cible');

    if (gameState.activePlayer !== 'player') {
      showError("Ce n'est pas votre tour");
      return;
    }
    if (gameState.pendingAction) {
      showError("Terminez d'abord l'action en cours");
      return;
    }
    if (gameState.phase !== 'main') {
      showError("Les sorts se lancent pendant votre phase principale");
      return;
    }
    // R34u : les gemmes (spellMana) complètent l'énergie pour les sorts
    if (card.cost > gameState.playerEnergy + (gameState.playerSpellMana || 0)) {
      showError(`Énergie insuffisante (${gameState.playerEnergy}+${gameState.playerSpellMana || 0}/${card.cost})`);
      return;
    }

    const needs = getSpellTargetNeeds(card);
    if (needs.needsSpecific) {
      if (!targetInfo || !targetInfo.targetInstanceId) {
        showError("Ce sort exige une cible: déposez-le sur une carte du terrain");
        return;
      }
      if (needs.needsEnemy && !needs.needsAlly && targetInfo.side !== 'enemy') {
        showError("Ce sort cible un monstre adverse");
        return;
      }
      if (needs.needsAlly && !needs.needsEnemy && targetInfo.side !== 'ally') {
        showError("Ce sort cible un de vos monstres");
        return;
      }
    }

    performGameAction('PLAY_SPELL', {
      cardInstanceId: card.instanceId,
      targetInstanceId: targetInfo ? targetInfo.targetInstanceId : undefined,
    }).then(res => {
      // Le serveur reste juge de paix: surface son message d'erreur éventuel
      if (res && res.success === false && res.message) {
        showError(res.message);
      }
      refreshGameState();
    }).catch(err => {
      console.error("[DragDrop] Erreur lors du lancement du sort:", err);
      showError("Erreur lors du lancement du sort");
    });
  }, [gameState, performGameAction, refreshGameState, showError]);

  // Gérer le drop dans la zone de monstre
  const handleDropInMonsterZone = useCallback((card, opts = {}) => {
    if (!card || !gameState) return;

    console.log(`[DragDrop] Carte déposée dans la zone de monstre:`, card);

    if (gameState.activePlayer !== 'player') {
      showError("Ce n'est pas votre tour");
      return;
    }

    if (card.cost > gameState.playerEnergy) {
      showError(`Énergie insuffisante (${gameState.playerEnergy}/${card.cost})`);
      return;
    }

    const monsterCount = gameState.playerField.filter(c => c.zone === 'monster').length;
    if (monsterCount >= 6) {
      showError("Votre zone de monstres est pleine");
      return;
    }

    const occupiedPositions = gameState.playerField
      .filter(c => c.zone === 'monster')
      .map(c => c.position);

    // POSITION CHOISIE AU POINTEUR : le slot occupe le rang correspondant a
    // l'endroit exact du drop (gauche / droite / entre deux cartes).
    let freePosition = null;
    {
      const dropX = (dragCurrentPosRef.current && dragCurrentPosRef.current.x) || null;
      const existing = Array.from(
        document.querySelectorAll('.player-monsters .monster-card[data-position]')
      ).map((el) => {
        const r = el.getBoundingClientRect();
        return { pos: +el.dataset.position, cx: r.left + r.width / 2 };
      }).sort((a, b) => a.pos - b.pos);

      let wantedIndex = existing.length; // drop a droite par defaut
      if (dropX !== null) {
        wantedIndex = existing.filter((e) => e.cx < dropX).length;
      }

      const freeSlots = [];
      for (let p = 0; p < 6; p++) {
        if (!occupiedPositions.includes(p)) freeSlots.push(p);
      }
      if (freeSlots.length > 0) {
        // rang d'un slot libre = nb de cartes existantes positionnees avant lui
        freePosition = freeSlots.reduce((best, p) => {
          const rank = occupiedPositions.filter((o) => o < p).length;
          const rankBest = occupiedPositions.filter((o) => o < best).length;
          return Math.abs(rank - wantedIndex) < Math.abs(rankBest - wantedIndex) ? p : best;
        }, freeSlots[0]);
      }
    }

    if (freePosition === null) {
      showError("Aucune position disponible");
      return;
    }

    console.log(`[DragDrop] Invocation de la carte ${card.id} à la position ${freePosition}`);

    // Point de départ des animations : position du pointeur au relâcher.
    // R34ll (réticule) : la carte part de la MAIN du joueur, pas du doigt.
    const dropPoint = opts.fromPoint
      ? { ...opts.fromPoint }
      : { ...dragCurrentPosRef.current };
    // Jetons déjà présents (pour détecter les nouveaux et les faire voler)
    const tokensBefore = new Set(
      Array.from(document.querySelectorAll('.player-monsters .monster-card.is-token'))
        .map(el => el.dataset.instanceId)
    );

    let ok = false;
    performGameAction('SUMMON', {
      cardId: card.id,
      position: freePosition
    }).then(res => {
      if (res && res.success === false && res.message) {
        showError(res.message);
        return null;
      }
      ok = true;
      return refreshGameState();
    }).then(() => {
      if (!ok) return;
      // Après le re-render React : la carte vole du point de drop jusqu'à SON slot
      requestAnimationFrame(() => requestAnimationFrame(() => {
        try {
          const slotEl = document.querySelector(
            `.player-monsters .monster-card[data-position="${freePosition}"]`
          );
          if (slotEl) {
            flyCardToElement({
              card,
              getCardElement,
              from: dropPoint,
              targetEl: slotEl,
              duration: 520,
              onLand: () => slotEl.classList.remove('just-summoned'),
            });
          }
          // Jetons invoqués par ON_SUMMON : ils volent depuis la carte invocatrice
          document.querySelectorAll('.player-monsters .monster-card.is-token').forEach(tokEl => {
            if (tokensBefore.has(tokEl.dataset.instanceId)) return;
            const fromEl = slotEl && slotEl !== tokEl ? slotEl : null;
            const src = fromEl
              ? {
                  x: fromEl.getBoundingClientRect().left + fromEl.getBoundingClientRect().width / 2,
                  y: fromEl.getBoundingClientRect().top + fromEl.getBoundingClientRect().height / 2,
                }
              : dropPoint;
            // Petit décalage pour que le jeton parte juste après la carte mère
            setTimeout(() => {
              flyCardToElement({
                card: { name: 'Jeton', type: 'CHARACTER', element: 'earth' },
                getCardElement,
                from: src,
                targetEl: tokEl,
                duration: 480,
                onLand: () => {
                  tokEl.classList.remove('just-summoned');
                  tokEl.classList.add('is-token'); // lueur verte conservée
                },
              });
            }, 200);
          });
        } catch (e) { /* animation décorative */ }
      }));
    }).catch(err => {
      console.error("[DragDrop] Erreur lors de l'invocation:", err);
      showError("Erreur lors de l'invocation");
    });
  }, [gameState, performGameAction, refreshGameState, showError, getCardElement]);

  // Gérer le drop dans la zone de bataille
  const handleDropInBattleZone = useCallback((card, targetInfo = null) => {
    if (!card || !gameState) return;

    console.log(`[DragDrop] Carte déposée dans la zone de combat`, targetInfo ? JSON.stringify(targetInfo) : 'sans cible');

    // R52 — Combattant : drop d'un monstre ADVERSE pendant notre attaque →
    // FORCE_ENGAGE. Le monstre est forcé en position de défense contre notre
    // premier FIGHTER prêt (verrouillé côté serveur jusqu'à la résolution).
    // Traité AVANT le guard de tour : l'attaquant garde la main pendant la
    // fenêtre de défense adverse (le serveur revalide le jeton d'attaque).
    const isOpponentMonster = card.owner === 'opponent' &&
      (gameState.opponentField || []).some(c => c.instanceId === (card.instanceId || card.id));
    if (isOpponentMonster) {
      if (!gameState.playerHasAttack) {
        showError("Vous n'avez pas le jeton d'attaque");
        return;
      }
      const defenders = (gameState.pendingAttack && gameState.pendingAttack.defenders) || [];
      const fighters = (gameState.playerField || []).filter(c =>
        c.zone === 'battle' && c.role === 'attacker' &&
        (c.abilities || []).includes('FIGHTER') &&
        !defenders.some(d => d.blocksInstanceId === c.instanceId));
      if (fighters.length === 0) {
        showError("Aucun Combattant prêt (déjà utilisé ou déjà défendu)");
        return;
      }
      console.log(`[DragDrop] FORCE_ENGAGE : ${fighters[0].name} force ${card.name} à défendre`);
      performGameAction('FORCE_ENGAGE', {
        cardInstanceId: fighters[0].instanceId,
        targetInstanceId: card.instanceId || card.id,
      }).then(refreshGameState)
        .catch(err => {
          console.error('[DragDrop] Erreur FORCE_ENGAGE:', err);
          showError("Forçage impossible (cible insaisissable ou déjà engagée)");
        });
      return;
    }

    if (gameState.activePlayer !== 'player') {
      showError("Ce n'est pas votre tour");
      return;
    }

    // Cas spécial de défense améliorée
    if (targetInfo && (targetInfo.type === 'defense' || targetInfo === 'defense')) {
      if (!gameState.opponentHasAttack) {
        showError("L'adversaire n'a pas le jeton d'attaque");
        return;
      }

      const attackersInBattle = gameState.opponentField.filter(c => c.zone === 'battle' && c.role === 'attacker');

      if (attackersInBattle.length === 0) {
        showError("Aucun attaquant à défendre");
        return;
      }

      let attackerPosition;
      let attackerInstance = null;

      if (targetInfo && targetInfo.attackerPosition !== undefined) {
        attackerPosition = targetInfo.attackerPosition;
        console.log(`[DragDrop] Défense avec position d'attaquant spécifiée: ${attackerPosition}`);

        const targetAttacker = attackersInBattle.find(a => a.position === attackerPosition);
        const existingDefenders = gameState.playerField.filter(c =>
          c.zone === 'battle' &&
          c.role === 'defender' &&
          c.defendingAgainst === attackerPosition
        );

        if (!targetAttacker) {
          showError("Attaquant non trouvé");
          return;
        }
        // R53e — Insaisissable (STEALTH/FLIGHT) : le drop est refusé AVANT
        // l'envoi serveur (le serveur refuse aussi, autorité finale).
        if (hasStealthAbility(targetAttacker) && !hasStealthAbility(card)) {
          showError(`${targetAttacker.name} est insaisissable — impossible de le bloquer`);
          return;
        }
        attackerInstance = targetAttacker.instanceId ?? null;

        if (existingDefenders.length > 0) {
          showError("Cet attaquant est déjà défendu");
          return;
        }
      } else {
        const defendedPositions = gameState.playerField
          .filter(c => c.zone === 'battle' && c.role === 'defender')
          .map(c => c.defendingAgainst);

        // R53e — la sélection auto saute aussi les attaquants insaisissables
        const undefendedAttackers = attackersInBattle.filter(a =>
          !defendedPositions.includes(a.position)
          && (hasStealthAbility(a) ? hasStealthAbility(card) : true));

        if (undefendedAttackers.length === 0) {
          showError("Aucun attaquant bloquable — tous sont défendus ou insaisissables");
          return;
        }

        attackerPosition = undefendedAttackers[0].position;
        attackerInstance = undefendedAttackers[0].instanceId ?? null;
        console.log(`[DragDrop] Défense contre premier attaquant non défendu: ${attackerPosition}`);
      }

      if (card.zone === 'battle') {
        showError("Ce monstre est déjà dans la zone de combat");
        return;
      }

      console.log(`[DragDrop] Préparation de la défense avec monstre position ${card.position} contre attaquant position ${attackerPosition}`);

      performGameAction('DEFEND', {
        defenderPosition: card.position,
        attackerPosition: attackerPosition,
        // InstanceId explicite : le serveur apparie le bloqueur par instanceId
        // (fallback position côté serveur pour compatibilité)
        attackerInstanceId: attackerInstance,
        cardInstanceId: card.instanceId
      }).then(refreshGameState)
        .catch(err => {
          console.error("[DragDrop] Erreur lors de la préparation de la défense:", err);
          showError("Erreur lors de la préparation de la défense");
        });

      return;
    }

    // Déclaration d'attaquant
    if (gameState.playerHasAttack) {
      if (gameState.phase !== 'battle') {
        console.log("[DragDrop] Passage automatique en phase de bataille");
        performGameAction('ATTACK_PHASE')
          .then(() => {
            if (card.zone === 'monster') {
              handleAttack(card, targetInfo);
            }
          })
          .catch(err => {
            console.error("[DragDrop] Erreur lors du passage en phase de bataille:", err);
            showError("Erreur lors du passage en phase de bataille");
          });
        return;
      }

      if (card.zone === 'monster') {
        handleAttack(card, targetInfo);
      } else if (card.zone === 'battle') {
        performGameAction('ATTACK', {
          position: card.position,
          targetPosition: targetInfo === 'opponent' ? 'direct' : null
        }).then(refreshGameState)
          .catch(err => {
            console.error("[DragDrop] Erreur lors de l'attaque:", err);
            showError("Erreur lors de l'attaque");
          });
      }
    }
    else if (gameState.opponentHasAttack) {
      showError("Vous devez défendre contre un attaquant spécifique");
    }
    else {
      showError("Aucun joueur ne peut attaquer pour le moment");
    }
  }, [gameState, performGameAction, refreshGameState, showError, handleAttack]);

  // Fonction générique pour le drop de carte (API HTML5 — zones BattleZone)
  const handleCardDrop = useCallback((e, zone, target = null) => {
    if (!draggingCard) return;

    console.log(`[DragDrop] Carte déposée dans la zone: ${zone}, cible: ${target}`);

    const isDefenseMode = gameState &&
      gameState.activePlayer === 'player' &&
      !gameState.playerHasAttack &&
      gameState.opponentHasAttack &&
      gameState.pendingAttack &&
      gameState.pendingAttack.attackers &&
      gameState.pendingAttack.attackers.length > 0;

    if (zone === 'battle' && isDefenseMode) {
      console.log(`[DragDrop] Défense détectée, cible:`, target);

      if (target && (target.type === 'defense' || typeof target.attackerPosition !== 'undefined')) {
        handleDropInBattleZone(draggingCard, target);
      } else {
        handleDropInBattleZone(draggingCard, { type: 'defense' });
      }
      return;
    }

    if (zone === 'battle') {
      const isDefense = gameState &&
        gameState.activePlayer === 'player' &&
        !gameState.playerHasAttack &&
        gameState.opponentHasAttack;

      if (isDefense) {
        if (target && (target.type === 'defense' || typeof target.attackerPosition !== 'undefined')) {
          handleDropInBattleZone(draggingCard, target);
        } else {
          handleDropInBattleZone(draggingCard, { type: 'defense' });
        }
      } else {
        handleDropInBattleZone(draggingCard, target);
      }
    } else if (zone === 'monster') {
      handleDropInMonsterZone(draggingCard);
    } else {
      console.log(`[DragDrop] Zone non reconnue: ${zone}`);
    }
  }, [draggingCard, handleDropInBattleZone, handleDropInMonsterZone, gameState]);

  // ═══════════ Événements de drag & drop ═══════════

  // Commencer le drag & drop (mousedown) — le fantôme n'apparaît qu'après
  // un déplacement suffisant (seuil) : un simple clic n'affiche rien.
  const handleCardDragStart = useCallback((e, card) => {
    e.stopPropagation();

    console.log("[DragDrop] Démarrage drag pour carte:", card);

    // Annuler un éventuel vol en cours ET supprimer son fantôme :
    // une annulation ne doit JAMAIS laisser un élément orphelin.
    if (flightCancelRef.current) {
      flightCancelRef.current.cancel();
      flightCancelRef.current = null;
    }
    killGhost();
    killReticle();

    const isDefenseMode = gameState &&
      gameState.activePlayer === 'player' &&
      !gameState.playerHasAttack &&
      gameState.opponentHasAttack &&
      gameState.pendingAttack &&
      gameState.pendingAttack.attackers &&
      gameState.pendingAttack.attackers.length > 0;

    // Vérifier que c'est le tour du joueur (ou mode défense)
    if (gameState && gameState.activePlayer !== 'player' && !isDefenseMode) {
      console.log("[DragDrop] Ce n'est pas votre tour, drag annulé");
      return;
    }

    // Énergie suffisante pour les cartes en main
    // R34u : gemmes utilisables pour les cartes non-monstre uniquement
    const _avail = (gameState?.playerEnergy || 0)
      + (card.type !== 'CHARACTER' ? (gameState?.playerSpellMana || 0) : 0);
    if (card.location === 'hand' && card.cost > _avail) {
      console.log(`[DragDrop] Énergie insuffisante (${gameState?.playerEnergy || 0}/${card.cost}), drag annulé`);
      flashUnaffordable(e.currentTarget, card);
      return;
    }

    // Conditions pour les monstres sur le terrain
    if (card.location === 'field' && card.zone === 'monster') {
      if (isDefenseMode) {
        console.log(`[DragDrop] Mode défense, autoriser le drag du monstre pour défendre`);
      } else {
        if (card.hasAttacked) {
          console.log(`[DragDrop] Ce monstre a déjà attaqué, drag annulé`);
          return;
        }

        if (!gameState?.playerHasAttack) {
          console.log(`[DragDrop] Vous n'avez pas le jeton d'attaque, drag annulé`);
          return;
        }
      }
    }

    let clientX, clientY;

    if (e.type.includes('touch')) {
      const touch = e.touches[0];
      clientX = touch.clientX;
      clientY = touch.clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    // Mémoriser la carte source (pour l'animation de retour)
    sourceElementRef.current = e.currentTarget || null;

    // Drag "en attente" : ne devient réel qu'après le seuil de déplacement
    pendingDragRef.current = {
      card,
      x: clientX,
      y: clientY,
      sourceEl: e.currentTarget || null,
    };
    isDraggingRef.current = false;

    dragStartPosRef.current = { x: clientX, y: clientY };
    dragCurrentPosRef.current = { x: clientX, y: clientY };

    // Réinitialiser le moteur de vélocité/tilt
    samplesRef.current = [{ x: clientX, y: clientY, t: performance.now() }];
    velocityRef.current = { x: 0, y: 0 };
    tiltRef.current = 0;
    targetTiltRef.current = 0;

    // Pour les événements HTML5 Drag & Drop
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';

      try {
        const transferData = JSON.stringify({
          id: card.id,
          position: card.position,
          location: card.location || 'hand',
          zone: card.zone || null,
          type: card.type
        });

        e.dataTransfer.setData('text/plain', transferData);

        const img = new Image();
        img.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
        e.dataTransfer.setDragImage(img, 0, 0);
      } catch (error) {
        console.error("[DragDrop] Erreur lors de la configuration du dataTransfer:", error);
      }
    }

    if (e.cancelable) e.preventDefault();

    return false;
  }, [gameState, killGhost, killReticle, flashUnaffordable]);

  // Passage du clic au drag réel : création du fantôme
  const beginDragIfNeeded = useCallback(() => {
    const pending = pendingDragRef.current;
    if (!pending || isDraggingRef.current) return;

    isDraggingRef.current = true;
    draggingCardRef.current = pending.card;
    setDraggingCard(pending.card);
    sourceElementRef.current = pending.sourceEl;

    // R34ll : cartes de la MAIN → réticule + ligne pointillée (la carte ne
        // quitte la main qu'au relâcher, lorsqu'elle vole vers sa destination).
    // Autres cartes (terrain/zone de combat) → fantôme classique.
    if (pending.card.location === 'hand') {
      ensureReticleStyles();
      killReticle();
      if (pending.sourceEl) {
        pending.sourceEl.classList.add('reticle-drag-source');
        reticleSourceElRef.current = pending.sourceEl;
      }
      reticleRef.current = createTargetReticle({ sourceEl: pending.sourceEl });
      const { x: sx, y: sy } = dragCurrentPosRef.current;
      reticleRef.current.moveTo(sx, sy);
      pendingDragRef.current = null;
      return;
    }

    const ghost = createGhostElement(pending.card, pending.sourceEl);
    const { x, y } = dragCurrentPosRef.current;
    ghost.style.left = `${x}px`;
    ghost.style.top = `${y}px`;
    ghost.style.transform = 'translate(-50%, -50%) scale(1.07)';
    startTiltLoop(ghost);

    pendingDragRef.current = null;
  }, [createGhostElement, startTiltLoop, killReticle]);

  // Détection de la zone de drop sous le curseur
  const findDropTarget = useCallback((card, x, y) => {
    const elementsAtDrop = document.elementsFromPoint(x, y);

    // ── Sorts: carte ciblée d'abord, sinon n'importe où sur le plateau ──
    if (isSpellCard(card)) {
      const monsterCard = elementsAtDrop.find(el =>
        el.classList && el.classList.contains('monster-card') &&
        el.dataset && el.dataset.instanceId
      );
      if (monsterCard) {
        const zone = monsterCard.closest('.monsters-zone');
        const side = zone && zone.classList.contains('opponent-monsters') ? 'enemy' : 'ally';
        return {
          zoneEl: monsterCard,
          kind: 'spell-target',
          side,
          targetInstanceId: monsterCard.dataset.instanceId,
        };
      }
      const boardArea = elementsAtDrop.find(el =>
        el.classList && (
          el.classList.contains('battle-zone') ||
          el.classList.contains('monsters-zone') ||
          el.classList.contains('battlefield')
        )
      );
      if (boardArea) return { zoneEl: boardArea, kind: 'spell-cast' };
      return null;
    }

    // Position de défense spécifique (slot d'attaquant)
    const defenseSlot = elementsAtDrop.find(el =>
      el.classList.contains('defense-position') || el.dataset?.attackerPosition !== undefined
    );
    if (defenseSlot) {
      return {
        zoneEl: defenseSlot,
        kind: 'defense-slot',
        attackerPosition: parseInt(defenseSlot.dataset.attackerPosition, 10),
      };
    }

    const battleZone = elementsAtDrop.find(el => el.classList.contains('battle-zone'));
    const monsterZone = elementsAtDrop.find(el => el.classList.contains('monsters-zone'));

    if (battleZone) {
      const isOpponentZone = battleZone.classList.contains('opponent-battle-zone');
      const isDefenseZone = battleZone.classList.contains('defense-zone');
      let targetInfo = isOpponentZone ? 'opponent' : null;
      if (isDefenseZone) targetInfo = 'defense';
      return { zoneEl: battleZone, kind: 'battle', targetInfo };
    }

    if (monsterZone) {
      const isPlayerZone = monsterZone.classList.contains('player-monsters');
      if (isPlayerZone && card.location === 'hand') {
        return { zoneEl: monsterZone, kind: 'monster' };
      }
    }

    return null;
  }, []);

  // Gérer le mouvement pendant le drag
  const handleCardDragMove = useCallback((e) => {
    const pending = pendingDragRef.current;

    if (!isDraggingRef.current && pending) {
      let clientX, clientY;
      if (e.type.includes('touch')) {
        const touch = e.touches[0];
        clientX = touch.clientX;
        clientY = touch.clientY;
      } else {
        clientX = e.clientX;
        clientY = e.clientY;
      }

      dragCurrentPosRef.current = { x: clientX, y: clientY };

      // Seuil : distinguer le clic du drag
      const dist = Math.hypot(clientX - pending.x, clientY - pending.y);
      if (dist < DRAG_THRESHOLD_PX) return;

      beginDragIfNeeded();
    }

    // R34ll : mode réticule — le réticule suit le doigt, feedback couleur
    const reticle = reticleRef.current;
    const ghost = activeGhostRef.current;
    if (!isDraggingRef.current || (!ghost && !reticle)) return;
    if (reticle) {
      // NB : ne PAS lire clientX/clientY ici — déclarés plus bas (TDZ)
      let rX = 0, rY = 0;
      if (e.type && e.type.includes('touch')) {
        const t = e.touches[0]; if (t) { rX = t.clientX; rY = t.clientY; }
      } else if (e.clientX !== undefined) { rX = e.clientX; rY = e.clientY; }
      dragCurrentPosRef.current = { x: rX, y: rY };
      reticle.moveTo(rX, rY);
      const rc = draggingCardRef.current;
      const rt = rc ? findDropTarget(rc, rX, rY) : null;
      reticle.setFeedback(evaluateDropFeedback(rc, rt, gameState));
    }
    if (!ghost) return;

    if (e.stopPropagation) e.stopPropagation();
    if (e.cancelable) e.preventDefault();

    let clientX, clientY;
    if (e.type.includes('touch')) {
      const touch = e.touches[0];
      clientX = touch.clientX;
      clientY = touch.clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    dragCurrentPosRef.current = { x: clientX, y: clientY };
    recordPointerSample(clientX, clientY);

    // Suivre le pointeur
    ghost.style.left = `${clientX}px`;
    ghost.style.top = `${clientY}px`;

    // Surbrillance des zones de drop valides
    const card = draggingCardRef.current;
    const target = card ? findDropTarget(card, clientX, clientY) : null;

    document.querySelectorAll('.battle-zone, .monsters-zone, .monster-card').forEach(zone => {
      zone.classList.remove('active-drop-target');
    });

    if (target) {
      target.zoneEl.classList.add('active-drop-target');
    }
  }, [findDropTarget, recordPointerSample, beginDragIfNeeded, gameState]);

  // Nettoyer les surlignages de zones
  const cleanupZoneHighlights = useCallback(() => {
    document.querySelectorAll('.battle-zone, .monsters-zone, .monster-card').forEach(zone => {
      zone.classList.remove('active-drop-target');
    });
  }, []);

  // Retour de la carte vers sa source (drop invalide)
  const returnCardToSource = useCallback((ghost, card) => {
    if (!ghost) return;
    const center = (el) => {
      const r = el.getBoundingClientRect();
      return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
    };

    let to = null;
    const src = sourceElementRef.current;
    if (src && src.isConnected) {
      to = center(src);
    }
    if (!to && card) {
      // La source a été re-rendue entre-temps : retrouver la carte dans la main
      const id = card.definitionId || card.id;
      const handCard = document.querySelector(`.player-hand .hand-card[data-card-id="${id}"]`);
      if (handCard) to = center(handCard);
    }
    if (!to) {
      // Dernier recours : retour visuel vers le bas de l'écran (zone main)
      to = { x: window.innerWidth / 2, y: window.innerHeight - 90 };
    }

    const removeGhost = () => ghost.remove();
    flightCancelRef.current = animateFlight(ghost, {
      from: { x: parseFloat(ghost.style.left) || 0, y: parseFloat(ghost.style.top) || 0 },
      to,
      velocity: { x: -velocityRef.current.x * 0.4, y: -velocityRef.current.y * 0.4 },
      duration: 380,
      rotation: { from: tiltRef.current, to: 0 },
      scale: { from: 1.07, to: 1 },
      onLand: removeGhost,
      onCancel: removeGhost,
    });
  }, []);

  // Gérer la fin du drag
  const handleCardDragEnd = useCallback((e) => {
    // Simple clic (pas de drag réel) — nettoyer l'état pending
    if (!isDraggingRef.current) {
      pendingDragRef.current = null;
      return;
    }

    console.log("[DragDrop] Fin du drag");

    isDraggingRef.current = false;
    pendingDragRef.current = null;
    stopTiltLoop();

    const card = draggingCardRef.current;
    const ghost = activeGhostRef.current;

    // Bloquer le click qui suit immédiatement le mouseup/touchend
    suppressNextClickRef.current = true;
    setTimeout(() => { suppressNextClickRef.current = false; }, 300);

    draggingCardRef.current = null;
    setDraggingCard(null);
    cleanupZoneHighlights();

    if (!card || (!ghost && !reticleRef.current)) {
      killGhost();
      killReticle();
      return;
    }

    // Coordonnées du relâcher
    let x, y;

    if (e && e.type && e.type.includes('touch') && e.changedTouches && e.changedTouches.length > 0) {
      const touch = e.changedTouches[0];
      x = touch.clientX;
      y = touch.clientY;
      console.log(`[DragDrop] Coordonnées tactiles du drop: ${x}, ${y}`);
    } else if (e && e.clientX !== undefined) {
      x = e.clientX;
      y = e.clientY;
    } else {
      // Sans coordonnées (blur, pointercancel) → annulation propre
      if (reticleRef.current) {
        reticleRef.current.dismiss();
        reticleRef.current = null;
        if (reticleSourceElRef.current) {
          reticleSourceElRef.current.classList.remove('reticle-drag-source');
          reticleSourceElRef.current = null;
        }
        return;
      }
      if (ghost) returnCardToSource(ghost, card);
      return;
    }

    const elementsAtDrop = document.elementsFromPoint(x, y);
    console.log(`[DragDrop] Éléments sous le drop:`, elementsAtDrop.map(el => el.className).join(', '));

    // ═══ R34ll : MODE RÉTICULE (carte de la main) ═══
    // La carte n'a jamais quitté la main : au relâcher, le réticule
    // s'effondre (animation), puis :
    //  - cible valide   → la carte VOLE de la main vers sa destination ;
    //  - cible invalide → la carte frémit en rouge dans la main (refus) ;
    //  - ailleurs       → rien (la carte reste posée dans la main).
    const reticle = reticleRef.current;
    if (reticle) {
      const feedback = reticle.feedback;
      const srcEl = reticleSourceElRef.current;
      reticleRef.current = null;
      const srcRect = srcEl && srcEl.isConnected ? srcEl.getBoundingClientRect() : null;
      const fromPoint = srcRect
        ? { x: srcRect.left + srcRect.width / 2, y: srcRect.top + srcRect.height / 2 }
        : { x, y };
      reticle.dismiss();
      if (srcEl) {
        srcEl.classList.remove('reticle-drag-source');
        reticleSourceElRef.current = null;
      }

      const target = findDropTarget(card, x, y);
      const fb = evaluateDropFeedback(card, target, gameState);

      if (fb !== 'valid' || !target) {
        // Refus : frémissement rouge de la carte dans la main
        if (fb === 'invalid' && srcEl) {
          srcEl.classList.add('reticle-drag-invalid');
          setTimeout(() => srcEl.classList.remove('reticle-drag-invalid'), 500);
        }
        return;
      }

      // Invocation : pas de vol préalable — handleDropInMonsterZone fait
      // déjà voler la carte main → slot exact APRES le rendu serveur.
      if (target.kind === 'monster') {
        handleDropInMonsterZone(card, { fromPoint });
        return;
      }

      // Cible valide : la carte sort de la main et file vers la zone
      const flyCard = document.createElement('div');
      flyCard.style.cssText =
        'position:fixed;pointer-events:none;z-index:10044;' +
        `left:${fromPoint.x}px;top:${fromPoint.y}px;width:${srcRect ? srcRect.width : 80}px;height:${srcRect ? srcRect.height : 120}px;` +
        'transform:translate(-50%,-50%);';
      const flyImg = document.createElement('img');
      const flyId = card && (card.cardId || card.definitionId || card.id);
      flyImg.src = card.imageUrl ||
        (flyId ? `/api/static/images/cards/${flyId}.webp` : '') ||
        `/api/static/images/cards/${getCardElement(card)}.svg`;
      flyImg.onerror = () => { flyImg.src = '/api/static/images/card-backs/fire-back.svg'; };
      flyImg.style.cssText = 'width:100%;height:100%;object-fit:cover;border-radius:6px;display:block;';
      flyCard.appendChild(flyImg);
      document.body.appendChild(flyCard);

      const r = target.zoneEl.getBoundingClientRect();
      const to = { x: r.left + r.width / 2, y: r.top + r.height / 2 };
      flightCancelRef.current = animateFlight(flyCard, {
        from: fromPoint,
        to,
        velocity: { x: 0.25, y: -0.15 },
        rotation: { from: 0, to: 0 },
        spin: 8,
        scale: { from: 1, to: 1 },
        duration: 480,
        onLand: () => {
          flashZone(target.zoneEl);
          flyCard.remove();
          switch (target.kind) {
            case 'spell-target':
            case 'spell-cast':
              handleCastSpell(card, target);
              break;
            case 'defense-slot':
              handleDropInBattleZone(card, { type: 'defense', attackerPosition: target.attackerPosition });
              break;
            case 'battle':
              handleDropInBattleZone(card, target.targetInfo);
              break;
            case 'monster':
              handleDropInMonsterZone(card, { fromPoint });
              break;
            default:
              break;
          }
        },
        onCancel: () => flyCard.remove(),
      });
      return;
    }


    // Une carte déjà engagée en zone de combat : soit attaque directe vers la
    // zone adverse (attaquant), soit RETRAIT libre vers la zone des monstres
    // tant que la confirmation n'est pas donnée (déclaration réversible).
    if (gameState && card.zone === 'battle' && card.role &&
      (gameState.phase === 'battle' || gameState.pendingAction === 'defend')) {
      const isInOpponentBattleZone = elementsAtDrop.some(el =>
        el.classList.contains('battle-zone') && el.classList.contains('opponent-battle-zone')
      );

      if (isInOpponentBattleZone && card.role === 'attacker') {
        console.log("[DragDrop] Attaque directe contre l'adversaire");
        const target = findDropTarget(card, x, y);
        if (target) {
          flightCancelRef.current = animateFlight(ghost, {
            from: { x, y },
            to: {
              x: target.zoneEl.getBoundingClientRect().left + target.zoneEl.getBoundingClientRect().width / 2,
              y: target.zoneEl.getBoundingClientRect().top + target.zoneEl.getBoundingClientRect().height / 2,
            },
            velocity: velocityRef.current,
            rotation: { from: tiltRef.current, to: 0 },
            spin: clamp(velocityRef.current.x * 10, -35, 35),
            scale: { from: 1.07, to: 1 },
            onLand: () => {
              flashZone(target.zoneEl);
              ghost.remove();
              handleDropInBattleZone(card, 'opponent');
            },
            onCancel: () => ghost.remove(),
          });
          return;
        }
      } else {
        // Retrait avant confirmation : la carte repart en zone des monstres
        const playerMonsterZone = elementsAtDrop.find(el =>
          el.classList && el.classList.contains('monsters-zone') && el.classList.contains('player-monsters')
        );

        if (playerMonsterZone) {
          const isDefense = card.role === 'defender';
          const r = playerMonsterZone.getBoundingClientRect();
          console.log(`[DragDrop] Annulation de déclaration (${isDefense ? 'défense' : 'attaque'}) pour ${card.name || card.id}`);
          flightCancelRef.current = animateFlight(ghost, {
            from: { x, y },
            to: { x: r.left + r.width / 2, y: r.top + r.height / 2 },
            velocity: velocityRef.current,
            rotation: { from: tiltRef.current, to: 0 },
            spin: clamp(velocityRef.current.x * 10, -35, 35),
            scale: { from: 1.07, to: 1 },
            onLand: () => {
              flashZone(playerMonsterZone);
              ghost.remove();
              performGameAction(isDefense ? 'UNDECLARE_DEFEND' : 'UNDECLARE_ATTACK', {
                cardInstanceId: card.instanceId,
              }).then(refreshGameState)
                .catch(err => {
                  console.error('[DragDrop] Erreur annulation de déclaration:', err);
                  showError("Impossible de retirer la carte de la zone de combat");
                });
            },
            onCancel: () => ghost.remove(),
          });
          return;
        }

        console.log("[DragDrop] Zone invalide pour une carte de combat (retour à la source)");
        returnCardToSource(ghost, card);
        return;
      }
    }

    // Résolution de la cible de drop
    const target = findDropTarget(card, x, y);

    if (!target) {
      console.log("[DragDrop] Drop en dehors d'une zone valide");
      returnCardToSource(ghost, card);
      return;
    }

    // Centre de la zone cible
    const r = target.zoneEl.getBoundingClientRect();
    const to = { x: r.left + r.width / 2, y: r.top + r.height / 2 };

    // Exécuter l'action adaptée à la cible
    const executeAction = () => {
      switch (target.kind) {
        case 'spell-target':
        case 'spell-cast':
          handleCastSpell(card, target);
          break;
        case 'defense-slot':
          handleDropInBattleZone(card, {
            type: 'defense',
            attackerPosition: target.attackerPosition
          });
          break;
        case 'battle':
          handleDropInBattleZone(card, target.targetInfo);
          break;
        case 'monster':
          handleDropInMonsterZone(card);
          break;
        default:
          break;
      }
    };

    // ═══ LANCER : la carte file avec la vitesse du doigt et retombe
    // en arc de cercle sur la zone cible (physique projectile) ═══
    flightCancelRef.current = animateFlight(ghost, {
      from: { x, y },
      to,
      velocity: velocityRef.current,
      rotation: { from: tiltRef.current, to: 0 },
      spin: clamp(velocityRef.current.x * 10, -35, 35),
      scale: { from: 1.07, to: 1 },
      onLand: () => {
        flashZone(target.zoneEl);
        ghost.remove();
        executeAction();
      },
      onCancel: () => ghost.remove(),
    });
  }, [
    gameState,
    handleDropInBattleZone, handleDropInMonsterZone, handleCastSpell,
    findDropTarget, returnCardToSource, cleanupZoneHighlights, stopTiltLoop,
    killGhost, killReticle, performGameAction, refreshGameState, showError
  ]);

  // Annuler une attaque/défense en cours
  const cancelPendingAction = useCallback((card) => {
    if (!gameState || !card) return;

    console.log(`[DragDrop] Annulation d'action pour la carte: ${card.id}`);

    try {
      if ((gameState.pendingAction && gameState.pendingAction.type === 'PREPARE_ATTACK') ||
        (gameState.pendingAction && gameState.pendingAction.type === 'PREPARE_DEFENSE')) {
        performGameAction('CANCEL_PENDING_ACTION', {
          cardId: card.id,
          position: card.position
        }).then(refreshGameState)
          .catch(err => {
            console.error("[DragDrop] Erreur lors de l'annulation de l'action:", err);
            showError("Erreur lors de l'annulation de l'action");
          });
      }
    } catch (error) {
      console.error("[DragDrop] Erreur lors de l'annulation:", error);
      showError("Une erreur est survenue");
    }
  }, [gameState, performGameAction, refreshGameState, showError]);

  // Gérer le clic sur une carte
  const handleCardClick = useCallback((card) => {
    if (!card) return;
    if (suppressNextClickRef.current) return;

    // Si la carte est en zone de bataille et qu'une action est en attente, annuler
    if (card.zone === 'battle' && (
      (gameState?.pendingAction?.type === 'PREPARE_ATTACK') ||
      (gameState?.pendingAction?.type === 'PREPARE_DEFENSE'))) {
      cancelPendingAction(card);
      return;
    }

    console.log("[DragDrop] Carte cliquée:", card);
  }, [gameState, cancelPendingAction]);

  // Système globalisé de drag & drop (souris + tactile)
  // TOUJOURS actif — y compris en mode dev (le drag de cartes est du gameplay).
  // Les listeners sont sur `window` : un relâcher hors battlefield, une perte
  // de focus ou un pointercancel terminent TOUJOURS le drag en cours.
  const setupCardDragHandlers = useCallback(() => {
    const handleMouseMove = (e) => {
      if (pendingDragRef.current || isDraggingRef.current) {
        handleCardDragMove(e);
      }
    };

    const handleMouseUp = (e) => {
      if (pendingDragRef.current || isDraggingRef.current) {
        console.log('[DragDrop] Mouse up détecté, fin du drag');
        handleCardDragEnd(e);
      }
    };

    const handleTouchMove = (e) => {
      if (pendingDragRef.current || isDraggingRef.current) {
        // Empêcher le scroll pendant le drag
        e.preventDefault();
        handleCardDragMove(e);
      }
    };

    const handleTouchEnd = (e) => {
      if (pendingDragRef.current || isDraggingRef.current) {
        console.log('[DragDrop] Touch end détecté, fin du drag');
        handleCardDragEnd(e);
      }
    };

    // Sécurités : perte de focus / annulation pointeur → fin propre du drag
    const handleAbort = () => {
      if (pendingDragRef.current || isDraggingRef.current) {
        console.log('[DragDrop] Drag interrompu (blur/pointercancel), annulation propre');
        handleCardDragEnd(null);
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd);
    window.addEventListener('blur', handleAbort);
    window.addEventListener('pointercancel', handleAbort);

    return {
      cleanup: () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
        window.removeEventListener('touchmove', handleTouchMove);
        window.removeEventListener('touchend', handleTouchEnd);
        window.removeEventListener('blur', handleAbort);
        window.removeEventListener('pointercancel', handleAbort);
      },
    };
  }, [handleCardDragMove, handleCardDragEnd]);

  return {
    draggingCard,
    handleCardDragStart,
    handleCardDragMove,
    handleCardDragEnd,
    handleCardDrop,
    handleAttack,
    handleCastSpell,
    handleCardClick,
    setupCardDragHandlers,
    getCardElement,
    suppressNextClickRef,
  };
};

export default useBattlefieldDragDrop;
